/**
 * Container for the /users list page. Owns the Page chrome and the list query;
 * useResourceListPageController owns the search input, debounce and URL sync.
 */

import { useState, useMemo, useRef, useCallback, useEffect, type CSSProperties } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useUsers, useUpdateUser } from '@hooks/users';
import { USERS_SEARCH_DEFAULTS, useUsersRouterSearch } from '@hooks/useUsersRouterSearch';
import { useTranslation } from '@hooks/useTranslation';
import { Counter } from '@/components/ui/counter';
import { Button } from '@/components/ui/button';
import { PlusIcon } from '../icons';
import { createUserColumns } from './columns';
import { USER_COLUMN_WIDTHS, USER_NAME_COLUMN_WIDTHS, USER_TABLE_MIN_WIDTH } from '../DataTable/columnWidths';
import { CreateUserDialog } from './CreateUserDialog';
import { ConfirmationDialog } from '../common/ConfirmationDialog';
import { UserPreviewSheet } from './UserPreviewSheet';
import { Page, PageTitle } from '../layouts/Page';
import { PageHeaderSearchBar, PageHeaderTotalLabel } from '../layouts/PageHeader';
import { ResponsivePageHeader } from '../layouts/ResponsivePageHeader';
import { openMobileSidebar } from '../MobileSidebar/mobileSidebarEvents';
import { useResourceListPageController } from '../ResourceList';
import { Toaster } from '@/components/ui/toaster';
import { buildStatusTabs } from './constants';
import { FilterChipsList, toChipGroups, type FilterCategory } from '@components/filters';
import { SEARCH_BAR_WIDTH } from '../../constants';
import {
  DataTableProvider,
  DataTableContent,
  DataTableToolbar,
  DataTableToolbarFilters,
  DataTableTabs,
  DataTableFacetedFilter,
  DataTableShortcuts,
  DataTableFooter,
  DEFAULT_PAGE_SIZE,
} from '../DataTable';
import type { SlingPosition, User, UsersResponse } from '@api/users';
import { can, CAPABILITIES, type CurrentUser } from '../../lib/capabilities';

interface UserManagementProps {
  /** The server's unfiltered first page, seeded by the Phlex view. */
  initialUsers?: UsersResponse;
  currentUser?: CurrentUser;
  slingPositions: SlingPosition[];
  onNewUser?: () => void;
  onEditUser?: (userId: number) => void;
  onDeactivateUser?: (userId: number) => void;
}

export default function UserManagement({
  initialUsers,
  currentUser,
  slingPositions,
}: UserManagementProps) {
  const { t, getSection } = useTranslation();
  // Hides write affordances only; the server policy stays the source of truth.
  const canWrite = can(currentUser, CAPABILITIES.USERS_WRITE);
  const minTableWidth = USER_TABLE_MIN_WIDTH - (canWrite ? 0 : USER_COLUMN_WIDTHS.actions);
  // Distinct from the search-params `navigate` below, which only edits the query string.
  const routerNavigate = useNavigate();

  // Note: translations are passed flat (not nested under 'users')
  const rolesTranslations = getSection<Record<string, string>>('roles');
  const tabsTranslations = getSection<Record<string, string>>('tabs');
  const statusTranslations = getSection<Record<string, string>>('status') || {};
  const columnsTranslations = getSection<Record<string, string>>('columns') || {};
  const actionsTranslations = getSection<Record<string, string>>('actions') || {};
  const filtersTranslations = getSection<Record<string, string>>('filters') || {};

  // Full vocabulary from the server. buildRoleOptions narrows to the assignable
  // ROLE_VALUES, which drops `system` and every role added since.
  const roleLabels = rolesTranslations ?? {};
  const statusTabs = buildStatusTabs(tabsTranslations);
  const router = useUsersRouterSearch();
  const { search, navigate, setStatus, setPage, setPerPage, setRoles } = router;

  // The seed is the server's unfiltered first page. Tab and role filter aren't
  // in the controller's search shape, so they're gated here and the predicate
  // below covers the rest.
  const {
    searchInput,
    setSearchInput,
    sorting,
    handleSortingChange,
    apiParams,
    initialDataForQuery,
  } = useResourceListPageController<string, UsersResponse>(
    router,
    search.status === USERS_SEARCH_DEFAULTS.status && search.roles.length === 0
      ? initialUsers
      : undefined,
    {
      isInitialDataEligible: (s) =>
        !s.q &&
        s.page === 1 &&
        s.perPage === DEFAULT_PAGE_SIZE &&
        s.sort === USERS_SEARCH_DEFAULTS.sort &&
        s.direction === USERS_SEARCH_DEFAULTS.direction,
    },
  );

  const updateUser = useUpdateUser();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [previewUserId, setPreviewUserId] = useState<number | null>(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [userToToggle, setUserToToggle] = useState<User | null>(null);
  // The status the confirmed action will save. The row menu toggles; the
  // drawer names a target outright, and for a Pending user those disagree.
  const [targetStatus, setTargetStatus] = useState<User['status'] | null>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const handleEditUser = useCallback(
    (userId: number) => {
      routerNavigate({ to: '/users/$id/edit', params: { id: String(userId) } });
    },
    [routerNavigate],
  );

  const handleRowClick = useCallback((user: User) => {
    setPreviewUserId(user.id);
  }, []);

  const handleRowSelect = useCallback((user: User, _rowIndex: number) => {
    handleRowClick(user);
  }, [handleRowClick]);

  const requestStatusChange = useCallback((user: User, next: User['status']) => {
    setUserToToggle(user);
    setTargetStatus(next);
    setStatusDialogOpen(true);
  }, []);

  // The row menu offers one action, so it names the opposite of where you are.
  const handleStatusToggleClick = useCallback(
    (user: User) => {
      requestStatusChange(user, user.status === 'verified' ? 'closed' : 'verified');
    },
    [requestStatusChange],
  );

  const handleStatusToggleConfirm = useCallback(async () => {
    if (!userToToggle || !targetStatus) return;

    setStatusDialogOpen(false);
    // Status only: resending the other fields once rewrote a system account's
    // role to admin.
    await updateUser.mutateAsync({ id: userToToggle.id, status: targetStatus });
  }, [userToToggle, targetStatus, updateUser]);

  // Mirror the server: a role it drops — or repeats — must not raise a chip here.
  // Own-key check, since `constructor` passes an `in` test.
  const selectedRoles = useMemo(
    () =>
      [...new Set(search.roles)].filter((role) =>
        Object.prototype.hasOwnProperty.call(roleLabels, role),
      ),
    [search.roles, roleLabels],
  );

  // Strip dead roles from the URL too, so a shared link can't claim a filter the
  // page isn't applying. `navigate`, not `setRoles`: a correction must not reset the page.
  useEffect(() => {
    if (selectedRoles.length !== search.roles.length) navigate({ roles: selectedRoles });
  }, [navigate, search.roles, selectedRoles]);

  const { data, isLoading, isFetching, isPlaceholderData, error } = useUsers({
    params: {
      status: search.status,
      q: apiParams.q,
      roles: selectedRoles,
      page: search.page,
      perPage: search.perPage,
      sortBy: search.sort || undefined,
      sortDirection: search.direction || undefined,
    },
    initialData: initialDataForQuery,
  });

  // Withhold the rows while keepPreviousData is serving the previous query's:
  // rendering them would list the old tab's people under the new tab's heading.
  // It also triggers the skeleton, which DataTableContent gates on an empty rows
  // array. No `?? initialUsers` fallback for the same reason — that pinned the
  // first tab's rows on screen permanently.
  const users = isPlaceholderData ? [] : (data?.users ?? []);
  // Keeps the last settled value, dimmed, rather than flashing 0 under the heading.
  const totalCount = data?.pagination.total ?? 0;
  const totalUnfiltered = data?.total_unfiltered ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / search.perPage));

  // Placeholder data carries the previous query's totals, so wait for this
  // query's to settle before clamping the page.
  useEffect(() => {
    if (data && !isPlaceholderData && search.page > totalPages) setPage(totalPages);
  }, [data, isPlaceholderData, search.page, totalPages, setPage]);

  const previewUser = useMemo(
    () => users.find((u) => u.id === previewUserId) ?? null,
    [users, previewUserId],
  );

  // Close the drawer if the selected user disappears from the list (e.g., after
  // a deactivation or a filter change)
  useEffect(() => {
    if (previewUserId !== null && !previewUser) {
      setPreviewUserId(null);
    }
  }, [previewUserId, previewUser]);

  const statusLabel = useMemo(() => {
    return statusTranslations[search.status];
  }, [search.status, statusTranslations]);

  // Every role the table can show, straight from the server's vocabulary: the
  // dropdown offers each one and the chips label from the same list, so a role
  // added in Ruby needs no frontend change.
  const roleOptions = useMemo(
    () => Object.entries(roleLabels).map(([value, label]) => ({ value, label })),
    [roleLabels],
  );

  const roleCategories = useMemo<FilterCategory[]>(
    () => [
      {
        key: 'role',
        label: filtersTranslations.role ?? '',
        options: roleOptions,
        selected: selectedRoles,
        onChange: setRoles,
      },
    ],
    [filtersTranslations.role, roleOptions, selectedRoles, setRoles],
  );

  const chipGroups = useMemo(() => toChipGroups(roleCategories), [roleCategories]);

  const columns = useMemo(
    () =>
      createUserColumns({
        columnLabels: columnsTranslations,
        actionLabels: actionsTranslations,
        roleLabels,
        currentUser,
        onEditUser: handleEditUser,
        onStatusToggle: handleStatusToggleClick,
      }),
    [handleEditUser, handleStatusToggleClick, columnsTranslations, actionsTranslations, roleLabels, currentUser],
  );

  return (
    <>
      <Page>
        <Page.Header className="py-6 md:py-0">
          <ResponsivePageHeader
            mobile={{
              title: t('page_title'),
              count: isLoading ? '…' : totalCount.toLocaleString(),
              searchValue: searchInput,
              onSearchChange: setSearchInput,
              searchPlaceholder: t('search.placeholder'),
              searchAriaLabel: t('search.aria_label'),
              openNavigationAriaLabel: t('header.open_navigation'),
              openSearchAriaLabel: t('header.open_search'),
              closeSearchAriaLabel: t('header.close_search'),
              onOpenNavigation: openMobileSidebar,
            }}
          >
            <div className="shrink-0">
              <PageHeaderTotalLabel count={totalUnfiltered} label={t('header.total_unfiltered')} />
            </div>
            <div
              className="flex-1 min-w-0 flex justify-center"
              style={{ flexBasis: SEARCH_BAR_WIDTH }}
            >
              <PageHeaderSearchBar
                value={searchInput}
                onChange={setSearchInput}
                placeholder={t('search.placeholder')}
                ariaLabel={t('search.aria_label')}
                shortcutKey="f"
                shortcutModifier="alt"
                shortcutAriaLabel={t('search.shortcut_aria')}
              />
            </div>
            <div
              className="min-w-0 invisible"
              aria-hidden="true"
              style={{ flexShrink: 99 }}
            >
              <PageHeaderTotalLabel count={totalUnfiltered} label={t('header.total_unfiltered')} />
            </div>
          </ResponsivePageHeader>
        </Page.Header>

        <Page.Body>
          <DataTableShortcuts>
            <div className="flex flex-col gap-4 h-full min-h-0 px-6 sm:px-8 lg:px-16">
              <PageTitle>
                <h1 className="font-kapra-expanded text-2xl font-black uppercase leading-tight text-off-black">
                  {statusLabel}
                </h1>
                <Counter value={totalCount} data-loading={isLoading || isFetching} />
              </PageTitle>

              {error && (
                <div className="p-4 bg-chilli-blush border border-chilli-red-light rounded-input flex-shrink-0">
                  <p className="font-inter text-sm leading-[1.5] text-chilli-red">{t('error')}</p>
                </div>
              )}

              <DataTableProvider
                columns={columns}
                data={users}
                pageCount={totalPages}
                manualSorting
                sorting={sorting}
                onSortingChange={handleSortingChange}
                isLoading={isLoading || isPlaceholderData}
                isFetching={isFetching}
              >
                <div
                  className="flex flex-col gap-4 flex-1 min-h-0 [container-type:inline-size]"
                  style={{
                    '--user-name-min': `${USER_NAME_COLUMN_WIDTHS.min}px`,
                    '--user-name-max': `${USER_NAME_COLUMN_WIDTHS.max}px`,
                    '--user-fixed-width': `${minTableWidth - USER_NAME_COLUMN_WIDTHS.min + 2}px`,
                  } as CSSProperties}
                >
                  <DataTableToolbar className="flex-shrink-0">
                    <DataTableTabs
                      tabs={statusTabs}
                      value={search.status}
                      onValueChange={(value) => setStatus(value as 'verified' | 'unverified' | 'closed')}
                    />
                    <DataTableToolbarFilters>
                      <DataTableFacetedFilter
                        title={filtersTranslations.role ?? ''}
                        options={roleOptions}
                        selected={selectedRoles}
                        onSelectedChange={setRoles}
                        shortcutIndex={3}
                      />
                      {canWrite && (
                        <Button onClick={() => setCreateDialogOpen(true)}>
                          <PlusIcon className="size-5" aria-hidden="true" />
                          {actionsTranslations.add_user ?? ''}
                        </Button>
                      )}
                    </DataTableToolbarFilters>
                  </DataTableToolbar>

                  <FilterChipsList groups={chipGroups} />

                  <DataTableContent
                    className="[&_col:first-child]:w-[clamp(var(--user-name-min),calc(100cqw_-_var(--user-fixed-width)),var(--user-name-max))]"
                    minTableWidth={minTableWidth}
                    emptyTitle={t('empty_state', '')}
                    onRowClick={handleRowClick}
                    onRowSelect={handleRowSelect}
                    tableRef={tableContainerRef}
                  />
                </div>
              </DataTableProvider>
            </div>
          </DataTableShortcuts>
        </Page.Body>

        <Page.Footer>
          <DataTableFooter
            currentPage={Math.min(search.page, totalPages)}
            totalPages={totalPages}
            totalCount={totalCount}
            perPage={search.perPage}
            onPageChange={setPage}
            onPerPageChange={setPerPage}
            className="flex flex-col justify-center h-full px-6 sm:px-8 lg:px-16"
          />
        </Page.Footer>

        <Toaster />
      </Page>

      <CreateUserDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        currentUser={currentUser}
        slingPositions={slingPositions}
      />

      <UserPreviewSheet
        user={previewUser}
        open={previewUserId !== null}
        onOpenChange={(open) => { if (!open) setPreviewUserId(null); }}
        currentUser={currentUser}
        onStatusChange={requestStatusChange}
      />

      <ConfirmationDialog
        open={statusDialogOpen}
        onOpenChange={setStatusDialogOpen}
        onConfirm={handleStatusToggleConfirm}
        title={t(
          targetStatus === 'verified'
            ? 'deactivate_dialog.title_activate'
            : 'deactivate_dialog.title_deactivate',
        )}
        message={t('deactivate_dialog.message', {
          action: t(
            targetStatus === 'verified'
              ? 'deactivate_dialog.action_activate'
              : 'deactivate_dialog.action_deactivate',
          ),
          name: userToToggle?.name ?? '',
        })}
        confirmLabel={t('dialog.confirm')}
        cancelLabel={t('dialog.cancel')}
        variant={targetStatus === 'verified' ? 'warning' : 'destructive'}
      />
    </>
  );
}
